import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { spouseService } from '../../../../services/spouse';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('SpouseInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value}</Text>
    <View style={styles.separator} />
  </View>
);

export const SpouseInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  const [spouseData, setSpouseData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSSN, setShowSSN] = useState(false);
  const [showDriversLicense, setShowDriversLicense] = useState(false);
  const [showPassportNumber, setShowPassportNumber] = useState(false);
  const [sensitiveData, setSensitiveData] = useState<any>({});

  useEffect(() => {
    loadSpouseData();
  }, [targetUserId]);

  const loadSpouseData = async () => {
    try {
      setLoading(true);
      const data = await spouseService.getSpouseInfo(targetUserId);

      if (data && data.length > 0) {
        // Get the most recent spouse entry
        const mostRecent = data[0];
        setSpouseData(mostRecent);

        // Load sensitive data from vault
        try {
          const credentials = await spouseService.getSpouseCredentials(mostRecent.id);
          setSensitiveData(credentials || {});
        } catch (error) {
          logger.warn('Could not load sensitive data from vault:', error);
          setSensitiveData({});
        }
      }
    } catch (error) {
      logger.error('Error loading spouse data:', error);
      Alert.alert('Error', 'Failed to load spouse information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatSSN = (ssn: string) => {
    if (!ssn) return '';
    const cleaned = ssn.replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
    }
    // Return as-is if already formatted or partial
    return ssn;
  };

  const formatDate = (date: Date | string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Spouse Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading spouse information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!spouseData) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Spouse Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly ? 'This user has not added spouse information yet' : 'No spouse information found'}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => navigation.navigate('SpouseInfoInput')}
              >
                <Text style={styles.addButtonText}>Add Spouse Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Spouse Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {spouseData.firstName && (
            <InfoItem label="First Name" value={spouseData.firstName || ''} />
          )}
          {spouseData.lastName && (
            <InfoItem label="Last Name" value={spouseData.lastName || ''} />
          )}
          {spouseData.maidenName && (
            <InfoItem label="Maiden Name" value={spouseData.maidenName || ''} />
          )}
          {spouseData.dateOfBirth && (
            <InfoItem label="Date of Birth" value={formatDate(spouseData.dateOfBirth)} />
          )}
          {spouseData.phoneNumber && (
            <InfoItem label="Phone Number" value={formatPhoneNumber(spouseData.phoneNumber) || ''} />
          )}
          {spouseData.email && (
            <InfoItem label="Email" value={spouseData.email || ''} />
          )}
          {spouseData.occupation && (
            <InfoItem label="Occupation" value={spouseData.occupation || ''} />
          )}
          {spouseData.employer && (
            <InfoItem label="Employer" value={spouseData.employer || ''} />
          )}
          {spouseData.workPhone && (
            <InfoItem label="Work Phone" value={formatPhoneNumber(spouseData.workPhone) || ''} />
          )}
          {spouseData.workEmail && (
            <InfoItem label="Work Email" value={spouseData.workEmail || ''} />
          )}
          {spouseData.workAddress && (
            <InfoItem label="Work Address" value={spouseData.workAddress || ''} />
          )}

          {sensitiveData.ssn && (
            <View style={styles.infoItem}>
              <Text style={styles.label}>SSN</Text>
              <View style={styles.valueRow}>
                <Text style={styles.value}>{showSSN ? formatSSN(sensitiveData.ssn) : '•••-••-••••'}</Text>
                <TouchableOpacity onPress={() => setShowSSN(!showSSN)} style={styles.eyeButton}>
                  <Image
                    source={showSSN ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                    style={styles.eyeIcon}
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.separator} />
            </View>
          )}

          {sensitiveData.driversLicense && (
            <View style={styles.infoItem}>
              <Text style={styles.label}>Driver's License</Text>
              <View style={styles.valueRow}>
                <Text style={styles.value}>{showDriversLicense ? sensitiveData.driversLicense : '••••••••••'}</Text>
                <TouchableOpacity onPress={() => setShowDriversLicense(!showDriversLicense)} style={styles.eyeButton}>
                  <Image
                    source={showDriversLicense ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                    style={styles.eyeIcon}
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.separator} />
            </View>
          )}

          {sensitiveData.passportNumber && (
            <View style={styles.infoItem}>
              <Text style={styles.label}>Passport Number</Text>
              <View style={styles.valueRow}>
                <Text style={styles.value}>{showPassportNumber ? sensitiveData.passportNumber : '••••••••••'}</Text>
                <TouchableOpacity onPress={() => setShowPassportNumber(!showPassportNumber)} style={styles.eyeButton}>
                  <Image
                    source={showPassportNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                    style={styles.eyeIcon}
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.separator} />
            </View>
          )}

          {spouseData.marriageDate && (
            <InfoItem label="Marriage Date" value={formatDate(spouseData.marriageDate)} />
          )}
          {spouseData.marriageLocation && (
            <InfoItem label="Marriage Location" value={spouseData.marriageLocation || ''} />
          )}
          {spouseData.emergencyContactName && (
            <InfoItem label="Emergency Contact Name" value={spouseData.emergencyContactName || ''} />
          )}
          {spouseData.emergencyContactPhone && (
            <InfoItem label="Emergency Contact Phone" value={formatPhoneNumber(spouseData.emergencyContactPhone) || ''} />
          )}
          {spouseData.emergencyContactRelationship && (
            <InfoItem label="Emergency Contact Relationship" value={spouseData.emergencyContactRelationship || ''} />
          )}
          {spouseData.medicalConditions && (
            <InfoItem label="Medical Conditions" value={spouseData.medicalConditions || ''} />
          )}
          {spouseData.allergies && (
            <InfoItem label="Allergies" value={spouseData.allergies || ''} />
          )}
          {spouseData.medications && (
            <InfoItem label="Medications" value={spouseData.medications || ''} />
          )}
          {spouseData.additionalNotes && (
            <InfoItem label="Additional Notes" value={spouseData.additionalNotes || ''} />
          )}

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('PersonalChecklist')}
            >
              <Text style={styles.buttonText}>Back</Text>
            </TouchableOpacity>

            {!viewOnly && (
              <>
                <TouchableOpacity
                  style={[styles.button, styles.editButton]}
                  onPress={() => {
                    logger.debug('[SpouseData Debug] Navigating to edit with data:', spouseData);
                    navigation.navigate('SpouseInfoInput', {
                      spouseData: spouseData,
                      isEditing: true
                    });
                  }}
                >
                  <Text style={styles.buttonText}>Edit</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.button, styles.deleteButton]}
                  onPress={() => {
                    Alert.alert('Delete', 'Are you sure you want to delete this spouse information?', [
                      { text: 'Cancel', style: 'cancel' },
                      { text: 'Delete', style: 'destructive', onPress: async () => {
                          try {
                            await spouseService.deleteSpouse(spouseData.id);

                            // CRITICAL: Check if this was the last entry and reset task if so
                            const remainingData = await spouseService.getAllSpouse();
                            if (!remainingData || remainingData.length === 0) {
                              await tasksService.resetTask('spouse_info');
                            }

                            Alert.alert('Deleted', 'Spouse information deleted.');
                            navigation.navigate('PersonalChecklist');
                          } catch (e) {
                            logger.error('Delete spouse failed:', e);
                            Alert.alert('Error', 'Failed to delete spouse information');
                          }
                        } },
                    ]);
                  }}
                >
                  <Text style={styles.buttonText}>Delete</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(8),
  },
  eyeButton: {
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(15),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  deleteButton: {
    backgroundColor: '#DC3545',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});